import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CalendarDaysIcon,
  UsersIcon,
  PlusIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  UserGroupIcon,
  DocumentArrowUpIcon,
  AcademicCapIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { ConfirmationModal } from "../components/ui/ConfirmationModal";
import { Badge } from "../components/ui/Badge";
import { SessionCard } from "../components/SessionCard";
import { StaffAssignment } from "../components/StaffAssignment";
import PlayerImport from "../components/PlayerImport";
import { GroupsSelectionModal } from "../components/GroupsSelectionModal";
import PlayerPhotoUpload from "../components/PlayerPhotoUpload";
import { apiService } from "../services/api";
import { formatLocalDate, formatTime } from "../utils/helpers";
import { useClubSettings } from "../contexts/ClubSettingsContext";
import { useAuth } from "../contexts/AuthContext";
import { RoleGuard } from "../components/ui/RoleGuard";

// PlayerRow component for inline editing
const PlayerRow = ({
  player,
  isEditing,
  onEdit,
  onCancel,
  onSave,
  onDataRefresh,
}) => {
  const [editForm, setEditForm] = useState({
    position: player.position || "",
    grade: player.grade || "",
    grad_year: player.grad_year || "",
    group: player.group || "",
    dob: player.dob || "",
  });

  const handleSave = () => {
    const updates = {};
    if (editForm.position !== (player.position || ""))
      updates.position = editForm.position;
    if (editForm.grade !== (player.grade || ""))
      updates.grade = editForm.grade ? parseInt(editForm.grade) : null;
    if (editForm.grad_year !== (player.grad_year || ""))
      updates.grad_year = editForm.grad_year
        ? parseInt(editForm.grad_year)
        : null;
    if (editForm.group !== (player.group || "")) updates.group = editForm.group;
    if (editForm.dob !== (player.dob || "")) updates.dob = editForm.dob;
    updates.photo_url = player.photo_url;
    onSave(updates);
  };

  const handleCancel = () => {
    setEditForm({
      position: player.position || "",
      grade: player.grade || "",
      grad_year: player.grad_year || "",
      group: player.group || "",
      dob: player.dob || "",
    });
    onCancel();
  };

  if (isEditing) {
    return (
      <tr>
        <td className="px-6 py-4 whitespace-nowrap">
          <PlayerPhotoUpload
            player={player}
            onPhotoUpdated={() => {
              // Refresh the evaluation data to show updated photo
              onDataRefresh && onDataRefresh();
            }}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-gray-900">
            {player.first_name} {player.last_name}
          </div>
          {player.email && (
            <div className="text-sm text-gray-500">{player.email}</div>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="text"
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
            value={editForm.position}
            onChange={(e) =>
              setEditForm({ ...editForm, position: e.target.value })
            }
            placeholder="Position"
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <select
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
            value={editForm.grade}
            onChange={(e) =>
              setEditForm({ ...editForm, grade: e.target.value })
            }
          >
            <option value="">Select Grade</option>
            <option value="9">Grade 9</option>
            <option value="10">Grade 10</option>
            <option value="11">Grade 11</option>
            <option value="12">Grade 12</option>
          </select>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="number"
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
            value={editForm.grad_year}
            onChange={(e) =>
              setEditForm({ ...editForm, grad_year: e.target.value })
            }
            placeholder="2025"
            min="2020"
            max="2030"
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="text"
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
            value={editForm.group}
            onChange={(e) =>
              setEditForm({ ...editForm, group: e.target.value })
            }
            placeholder="Group"
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="date"
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
            value={editForm.dob}
            onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex space-x-2">
            <Button size="sm" onClick={handleSave}>
              Save
            </Button>
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex-shrink-0 h-10 w-10">
          {player.photo_url ? (
            <img
              className="h-10 w-10 rounded-full object-cover"
              src={player.photo_url}
              alt={`${player.first_name} ${player.last_name}`}
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600">
                {player.first_name?.charAt(0)?.toUpperCase() || "?"}
              </span>
            </div>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {player.first_name} {player.last_name}
        </div>
        {player.email && (
          <div className="text-sm text-gray-500">{player.email}</div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {player.position || "-"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {player.grade || "-"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {player.grad_year || "-"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {player.group || "-"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {player.dob || "-"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <Button size="sm" variant="outline" onClick={onEdit}>
          Edit
        </Button>
      </td>
    </tr>
  );
};

const EvaluationSetup = () => {
  const { clubId, evalId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [evaluation, setEvaluation] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [skillCategories, setSkillCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || "overview",
  );

  // Modal states
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPlayerImportModal, setShowPlayerImportModal] = useState(false);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [showDeleteSessionModal, setShowDeleteSessionModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [deletingSession, setDeletingSession] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [playerLimitInfo, setPlayerLimitInfo] = useState(null); // { current, limit }
  const [featureFlags, setFeatureFlags] = useState(null); // null = all enabled

  // Filter states
  const [filterText, setFilterText] = useState("");
  const [filterPosition, setFilterPosition] = useState("");
  const [filterGrade, setFilterGrade] = useState("");
  const [filterGradYear, setFilterGradYear] = useState("");
  const [filterGroup, setFilterGroup] = useState("");

  const { evaluationType } = useClubSettings();

  // Forms
  const [sessionForm, setSessionForm] = useState({
    name: "",
    session_date: "",
    start_time: "",
    end_time: "",
    location: "",
    max_participants: "",
    notes: "",
  });

  const [evaluationForm, setEvaluationForm] = useState({
    name: "",
    season: "",
    year: new Date().getFullYear(),
    description: "",
    start_date: "",
    end_date: "",
    assignment_mode: "open",
  });

  const loadEvaluationData = useCallback(async () => {
    try {
      const [evalData, sessionsData, playersData, staffData, categoriesData] =
        await Promise.all([
          apiService.getEvaluation(evalId),
          apiService.getEvaluationSessions(evalId),
          apiService.getEvaluationPlayers(evalId),
          apiService.getClubStaff(clubId),
          apiService.getClubSkillCategories(clubId),
        ]);
      setEvaluation(evalData);
      setSessions(sessionsData);
      setAvailablePlayers(playersData);
      setAvailableStaff(staffData);
      setSkillCategories(categoriesData);

      setEvaluationForm({
        name: evalData.name,
        season: evalData.season || "",
        year: evalData.year || new Date().getFullYear(),
        description: evalData.description || "",
        start_date: evalData.start_date || new Date().getFullYear(),
        end_date: evalData.end_date || new Date().getFullYear(),
        assignment_mode: evalData.assignment_mode || "open",
      });
    } catch (error) {
      console.error("Error loading evaluation data:", error);
      toast.error("Failed to load evaluation data");
    }
    setLoading(false);
  }, [evalId, clubId]);

  useEffect(() => {
    loadEvaluationData();
    apiService.getClubSubscriptionLimits(clubId)
      .then((limits) => {
        setPlayerLimitInfo({ current: limits.players.current, limit: limits.players.limit });
        setFeatureFlags(limits.feature_flags ?? null);
      })
      .catch((err) => console.warn("Could not load subscription limits:", err));
  }, [loadEvaluationData, clubId]);

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      const sessionData = {
        ...sessionForm,
        evaluation_id: evalId,
        max_participants: sessionForm.max_participants
          ? parseInt(sessionForm.max_participants)
          : null,
      };
      if (editingSession) {
        await apiService.updateSession(editingSession.id, sessionData);
        toast.success("Session updated successfully!");
      } else {
        await apiService.createSession(sessionData);
        toast.success("Session created successfully!");
      }

      setShowSessionModal(false);
      setEditingSession(null);
      setSessionForm({
        name: "",
        session_date: "",
        start_time: "",
        end_time: "",
        location: "",
        max_participants: "",
        notes: "",
      });
      await loadEvaluationData();
    } catch (error) {
      toast.error("Failed to save session");
    }
  };

  const handleEditSession = (session) => {
    setEditingSession(session);
    setSessionForm({
      name: session.name,
      session_date: session.session_date,
      start_time: session.start_time || "",
      end_time: session.end_time || "",
      location: session.location || "",
      max_participants: session.max_participants || "",
      notes: session.notes || "",
    });
    setShowSessionModal(true);
  };

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;

    setDeletingSession(true);
    try {
      await apiService.deleteSession(sessionToDelete.id);
      toast.success("Session deleted successfully");
      setShowDeleteSessionModal(false);
      setSessionToDelete(null);
      await loadEvaluationData();
    } catch (error) {
      toast.error("Failed to delete session");
    }
    setDeletingSession(false);
  };

  const handleDeleteSessionClick = (session) => {
    setSessionToDelete(session);
    setShowDeleteSessionModal(true);
  };

  const handleUpdateEvaluation = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        clubId: clubId,
        ...evaluationForm,
      };
      await apiService.updateEvaluation(evalId, updateData);
      setEvaluation((prev) => ({ ...prev, ...evaluationForm }));
      setShowSettingsModal(false);
      toast.success("Evaluation updated successfully!");
    } catch (error) {
      toast.error("Failed to update evaluation");
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleEvaluationDelete = async () => {
    setDeleting(true);
    try {
      await apiService.deleteEvaluation(evaluation.id);
      setShowDeleteModal(false);
      navigate(`/portal/club/${clubId}/evaluations`);
    } catch (error) {
      console.error("Error deleting evaluation:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdatePlayer = useCallback(
    async (playerId, updates) => {
      try {
        await apiService.updatePlayer(playerId, updates);
        setEditingPlayer(null);
        await loadEvaluationData();
        toast.success("Player updated successfully!");
      } catch (error) {
        console.error("Error updating player:", error);
        toast.error("Failed to update player");
      }
    },
    [loadEvaluationData],
  );

  const handleAddSkills = async (session) => {
    setSelectedSession(session);
    const sessionDetails = await apiService.getSessionDetails(session.id);
    const existingCategories = sessionDetails.session_skill_categories.map(
      (cat) => cat.category_id,
    );
    setSelectedCategories(existingCategories);
    setShowSkillsModal(true);
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId],
    );
  };

  const handleSaveSkills = async () => {
    if (selectedCategories.length === 0) {
      toast.error("Please select at least one skill category");
      return;
    }
    try {
      await apiService.addSessionSkillCategories(selectedSession.id, {
        skillCategories: selectedCategories,
      });
      toast.success("Skill categories added to session successfully!");
      setShowSkillsModal(false);
      setSelectedSession(null);
      setSelectedCategories([]);
      await loadEvaluationData();
    } catch (error) {
      console.error("Error adding skills to session:", error);
      toast.error("Failed to add skill categories");
    }
  };

  const handleAddGroups = (session) => {
    setSelectedSession(session);
    setShowGroupsModal(true);
  };

  const getFilteredPlayers = useMemo(() => {
    return availablePlayers
      .filter((player) => {
        const fullName =
          `${player.first_name} ${player.last_name}`.toLowerCase();
        const matchesText =
          !filterText ||
          fullName.includes(filterText.toLowerCase()) ||
          (player.email &&
            player.email.toLowerCase().includes(filterText.toLowerCase()));

        const matchesPosition =
          !filterPosition ||
          (filterPosition === "__blank__"
            ? !player.position ||
              player.position === "" ||
              player.position === "-"
            : player.position === filterPosition);

        const matchesGrade =
          !filterGrade ||
          (filterGrade === "__blank__"
            ? !player.grade || player.grade === ""
            : player.grade?.toString() === filterGrade);

        const matchesGradYear =
          !filterGradYear ||
          (filterGradYear === "__blank__"
            ? !player.grad_year || player.grad_year === ""
            : player.grad_year?.toString() === filterGradYear);

        const matchesGroup =
          !filterGroup ||
          (filterGroup === "__blank__"
            ? !player.group || player.group === "" || player.group === "-"
            : player.group === filterGroup);

        return (
          matchesText &&
          matchesPosition &&
          matchesGrade &&
          matchesGradYear &&
          matchesGroup
        );
      })
      .sort((a, b) => {
        // Sort by last name, then by first name
        const lastNameA = (a.last_name || "").toLowerCase();
        const lastNameB = (b.last_name || "").toLowerCase();

        if (lastNameA < lastNameB) return -1;
        if (lastNameA > lastNameB) return 1;

        // If last names are the same, sort by first name
        const firstNameA = (a.first_name || "").toLowerCase();
        const firstNameB = (b.first_name || "").toLowerCase();

        if (firstNameA < firstNameB) return -1;
        if (firstNameA > firstNameB) return 1;

        return 0;
      });
  }, [
    availablePlayers,
    filterText,
    filterPosition,
    filterGrade,
    filterGradYear,
    filterGroup,
  ]);

  const getUniqueValues = useMemo(() => {
    return (field) => {
      const values = availablePlayers
        .map((player) => player[field])
        .filter((value) => value && value !== "")
        .filter((value, index, self) => self.indexOf(value) === index);
      return values.sort();
    };
  }, [availablePlayers]);

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "green";
      case "completed":
        return "blue";
      case "scheduled":
        return "yellow";
      case "cancelled":
        return "red";
      default:
        return "gray";
    }
  };

  const getTabContent = () => {
    switch (activeTab) {
      case "sessions":
        return <SessionsTab />;
      case "players":
        return PlayersTab;
      case "staff":
        return <StaffTab />;
      default:
        return <OverviewTab />;
    }
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Evaluation Info */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Evaluation Details</CardTitle>
            {evaluation.status !== "completed" && (
                <div className="flex items-center space-x-2">
                  <RoleGuard roles={["super_admin", "club_admin", "coach"]}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const resultsPath =
                          evaluationType === "skills"
                            ? `/portal/evaluation/${evalId}/skill-results`
                            : `/portal/evaluation/${evalId}/results`;
                        navigate(resultsPath);
                      }}
                    >
                      <EyeIcon className="w-4 h-4 mr-2" />
                      View Results
                    </Button>
                  </RoleGuard>
                  <RoleGuard roles={["super_admin", "club_admin"]}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowSettingsModal(true)}
                    >
                      <PencilIcon className="w-4 h-4 mr-2" />
                      Edit Details
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={handleDeleteClick}
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </RoleGuard>
                </div>
              )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                Basic Information
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium">{evaluation?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Season:</span>
                  <span className="font-medium">
                    {evaluation?.season || "Not set"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Year:</span>
                  <span className="font-medium">
                    {evaluation?.year || "Not set"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <Badge variant={getStatusColor(evaluation?.status)}>
                    {evaluation?.status?.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                Access Information
              </h4>
              <div className="bg-gray-50 rounded-md p-3">
                <p className="text-sm text-gray-600 mb-1">
                  Mobile App Access Code
                </p>
                <p className="text-lg font-mono font-bold text-slate-600">
                  {evaluation?.access_code}
                </p>
              </div>
            </div>
          </div>
          {evaluation?.description && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">Description</h4>
              <p className="text-sm text-gray-600">{evaluation.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CalendarDaysIcon className="h-8 w-8 text-slate-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">
                  Total Sessions
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {sessions.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {sessions.filter((s) => s.status === "completed").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">
                  Available Players
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {availablePlayers.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <UsersIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">
                  Staff Assigned
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {availableStaff.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Sessions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No sessions yet
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Create your first evaluation session to get started.
              </p>
              <div className="mt-6">
                <Button onClick={() => setShowSessionModal(true)}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create First Session
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.slice(0, 3).map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <CalendarDaysIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {session.name}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{formatLocalDate(session.session_date)}</span>
                        {session.start_time && (
                          <span>{formatTime(session.start_time)}</span>
                        )}
                        {session.location && <span>📍 {session.location}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant={getStatusColor(session.status)}>
                      {session.status}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      {session.checkin_count || 0} checked in
                    </span>
                  </div>
                </div>
              ))}
              {sessions.length > 3 && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setActiveTab("sessions")}
                >
                  View All Sessions ({sessions.length})
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const SessionsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          Evaluation Sessions ({sessions.length})
        </h2>
        <RoleGuard roles={["super_admin", "club_admin"]}>
          <Button onClick={() => setShowSessionModal(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Create Session
          </Button>
        </RoleGuard>
      </div>

      {sessions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No sessions created
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Create evaluation sessions to organize your player assessments.
            </p>
            <div className="mt-6">
              <Button onClick={() => setShowSessionModal(true)}>
                Create First Session
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              onEdit={handleEditSession}
              onDelete={handleDeleteSessionClick}
              onAddSkills={handleAddSkills}
              onAddGroups={handleAddGroups}
              availablePlayers={availablePlayers}
              availableStaff={availableStaff}
              evaluationType={evaluationType}
            />
          ))}
        </div>
      )}
    </div>
  );

  const PlayersTab = useMemo(() => {
    const filteredPlayers = getFilteredPlayers;
    const positions = getUniqueValues("position");
    const grades = getUniqueValues("grade");
    const gradYears = getUniqueValues("grad_year");
    const groups = getUniqueValues("group");

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Player Management ({availablePlayers.length} available)
          </h2>
          {(() => {
            const limit = playerLimitInfo?.limit;
            const current = playerLimitInfo?.current ?? availablePlayers.length;
            const atLimit = limit != null && current >= limit;
            return (
              <div className="relative group">
                <Button
                  onClick={atLimit ? undefined : () => setShowPlayerImportModal(true)}
                  disabled={atLimit}
                >
                  <DocumentArrowUpIcon className="w-4 h-4 mr-2" />
                  Import Players
                </Button>
                {atLimit && (
                  <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                    Your plan allows {limit} player{limit === 1 ? "" : "s"}. Upgrade your plan to add more.
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {availablePlayers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No players registered
              </h3>
              {(() => {
                const limit = playerLimitInfo?.limit;
                const current = playerLimitInfo?.current ?? 0;
                const atLimit = limit != null && current >= limit;
                return atLimit ? (
                  <p className="mt-1 text-sm text-amber-600">
                    Your plan allows {limit} player{limit === 1 ? "" : "s"} and your club has reached that limit. Upgrade your plan to add more.
                  </p>
                ) : (
                  <>
                    <p className="mt-1 text-sm text-gray-500">
                      Import players who have signed up for this evaluation.
                    </p>
                    <div className="mt-6">
                      <Button onClick={() => setShowPlayerImportModal(true)}>
                        <DocumentArrowUpIcon className="w-4 h-4 mr-2" />
                        Import First Players
                      </Button>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Filters */}
            <Card>
              <CardHeader>
                <CardTitle>Filter Players</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <Input
                    label="Search Name"
                    placeholder="Search players..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Position
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                      value={filterPosition}
                      onChange={(e) => setFilterPosition(e.target.value)}
                    >
                      <option value="">All Positions</option>
                      <option value="__blank__">Blank</option>
                      {positions.map((position) => (
                        <option key={position} value={position}>
                          {position}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grade
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                      value={filterGrade}
                      onChange={(e) => setFilterGrade(e.target.value)}
                    >
                      <option value="">All Grades</option>
                      <option value="__blank__">Blank</option>
                      {grades.map((grade) => (
                        <option key={grade} value={grade}>
                          Grade {grade}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grad Year
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                      value={filterGradYear}
                      onChange={(e) => setFilterGradYear(e.target.value)}
                    >
                      <option value="">All Grad Years</option>
                      <option value="__blank__">Blank</option>
                      {gradYears.map((gradYear) => (
                        <option key={gradYear} value={gradYear}>
                          {gradYear}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Group
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
                      value={filterGroup}
                      onChange={(e) => setFilterGroup(e.target.value)}
                    >
                      <option value="">All Groups</option>
                      <option value="__blank__">Blank</option>
                      {groups.map((group) => (
                        <option key={group} value={group}>
                          {group}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {(filterText ||
                  filterPosition ||
                  filterGrade ||
                  filterGradYear ||
                  filterGroup) && (
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      Showing {filteredPlayers.length} of{" "}
                      {availablePlayers.length} players
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFilterText("");
                        setFilterPosition("");
                        setFilterGrade("");
                        setFilterGradYear("");
                        setFilterGroup("");
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Player List */}
            <Card>
              <CardHeader>
                <CardTitle>
                  Registered Players ({filteredPlayers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Photo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Position
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Grade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Grad Year
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Group
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          DOB
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredPlayers.map((player) => (
                        <PlayerRow
                          key={player.id}
                          player={player}
                          isEditing={editingPlayer === player.id}
                          onEdit={() => setEditingPlayer(player.id)}
                          onCancel={() => setEditingPlayer(null)}
                          onSave={(updates) =>
                            handleUpdatePlayer(player.id, updates)
                          }
                          onDataRefresh={loadEvaluationData}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }, [
    availablePlayers,
    getFilteredPlayers,
    getUniqueValues,
    filterText,
    filterPosition,
    filterGrade,
    filterGradYear,
    filterGroup,
    editingPlayer,
    setShowPlayerImportModal,
    setEditingPlayer,
    handleUpdatePlayer,
    loadEvaluationData,
    playerLimitInfo,
  ]);

  const StaffTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          Staff Assignments ({availableStaff.length} available)
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sessions.map((session) => (
          <StaffAssignment
            key={session.id}
            session={session}
            availableStaff={availableStaff}
            onUpdate={loadEvaluationData}
          />
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-bold text-gray-900">
              {evaluation?.name}
            </h1>
            <Badge variant={getStatusColor(evaluation?.status)}>
              {evaluation?.status?.toUpperCase()}
            </Badge>
          </div>
          <p className="text-gray-600 mt-1">
            {evaluation?.season} {evaluation?.year} • {sessions.length} sessions
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(`/portal/club/${clubId}/evaluations`)}
        >
          Back to All Evaluations
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "overview", name: "Overview", icon: Cog6ToothIcon },
            { id: "sessions", name: "Sessions", icon: CalendarDaysIcon },
            { id: "players", name: "Players", icon: UserGroupIcon },
            { id: "staff", name: "Staff", icon: UsersIcon },
          ]
            .filter((tab) => {
              if (user.role === "coordinator" || user.role === "coach") {
                return tab.id === "overview" || tab.id === "sessions";
              }
              if (tab.id === "staff") {
                return (
                  user.role === "club_admin" || user.role === "super_admin"
                );
              }
              return true;
            })
            .map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-slate-500 text-slate-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.name}
              </button>
            ))}
        </nav>
      </div>

      {/* Tab Content */}
      {getTabContent()}

      {/* Create/Edit Session Modal */}
      <Modal
        isOpen={showSessionModal}
        onClose={() => {
          setShowSessionModal(false);
          setEditingSession(null);
          setSessionForm({
            name: "",
            session_date: "",
            start_time: "",
            end_time: "",
            location: "",
            max_participants: "",
            notes: "",
          });
        }}
        title={editingSession ? "Edit Session" : "Create New Session"}
        size="lg"
      >
        <form onSubmit={handleCreateSession} className="space-y-4">
          <Input
            label="Session Name"
            placeholder="e.g., Tryout Session 1, Skills Assessment"
            value={sessionForm.name}
            onChange={(e) =>
              setSessionForm({ ...sessionForm, name: e.target.value })
            }
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Date"
              type="date"
              value={sessionForm.session_date}
              onChange={(e) =>
                setSessionForm({ ...sessionForm, session_date: e.target.value })
              }
              required
            />
            <Input
              label="Start Time"
              type="time"
              value={sessionForm.start_time}
              onChange={(e) =>
                setSessionForm({ ...sessionForm, start_time: e.target.value })
              }
            />
            <Input
              label="End Time"
              type="time"
              value={sessionForm.end_time}
              onChange={(e) =>
                setSessionForm({ ...sessionForm, end_time: e.target.value })
              }
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Location"
              placeholder="e.g., Main Field, Gym A"
              value={sessionForm.location}
              onChange={(e) =>
                setSessionForm({ ...sessionForm, location: e.target.value })
              }
            />
            <Input
              label="Max Participants"
              type="number"
              placeholder="Leave empty for no limit"
              value={sessionForm.max_participants}
              onChange={(e) =>
                setSessionForm({
                  ...sessionForm,
                  max_participants: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
              rows="3"
              placeholder="Any additional information about this session..."
              value={sessionForm.notes}
              onChange={(e) =>
                setSessionForm({ ...sessionForm, notes: e.target.value })
              }
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowSessionModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit">
              {editingSession ? "Update Session" : "Create Session"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Evaluation Settings Modal */}
      <Modal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        title="Evaluation Settings"
      >
        <form onSubmit={handleUpdateEvaluation} className="space-y-4">
          <Input
            label="Evaluation Name"
            value={evaluationForm.name}
            onChange={(e) =>
              setEvaluationForm({ ...evaluationForm, name: e.target.value })
            }
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Season"
              placeholder="Fall, Spring, Summer"
              value={evaluationForm.season}
              onChange={(e) =>
                setEvaluationForm({ ...evaluationForm, season: e.target.value })
              }
            />
            <Input
              label="Year"
              type="number"
              value={evaluationForm.year}
              onChange={(e) =>
                setEvaluationForm({
                  ...evaluationForm,
                  year: parseInt(e.target.value),
                })
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              value={evaluationForm.start_date}
              onChange={(e) =>
                setEvaluationForm({
                  ...evaluationForm,
                  start_date: e.target.value,
                })
              }
            />
            <Input
              label="End Date"
              type="date"
              value={evaluationForm.end_date}
              onChange={(e) =>
                setEvaluationForm({
                  ...evaluationForm,
                  end_date: e.target.value,
                })
              }
            />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <p className="text-sm text-slate-800">
              <strong>Start Date:</strong> First day of evaluations
              <br />
              <strong>End Date:</strong> Last day of evaluations
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
              rows="3"
              placeholder="Describe the purpose and goals of this evaluation..."
              value={evaluationForm.description}
              onChange={(e) =>
                setEvaluationForm({
                  ...evaluationForm,
                  description: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Check-in Workflow
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
              value={evaluationForm.assignment_mode}
              onChange={(e) =>
                setEvaluationForm({
                  ...evaluationForm,
                  assignment_mode: e.target.value,
                })
              }
            >
              <option value="open">
                Open Check-in (players can be checked in without pre-assignment)
              </option>
              <option value="assigned">
                Pre-assigned (players must be assigned to sessions first)
              </option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              This sets the default workflow for new sessions. You can change
              the workflow for individual sessions if needed.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowSettingsModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      </Modal>

      {/* Player Import Modal */}
      <Modal
        isOpen={showPlayerImportModal}
        onClose={() => setShowPlayerImportModal(false)}
        title="Import Players"
        size="lg"
      >
        <PlayerImport
          evaluationId={evalId}
          onComplete={(importedPlayers) => {
            setShowPlayerImportModal(false);
            loadEvaluationData();
          }}
          onCancel={() => setShowPlayerImportModal(false)}
          featureFlags={featureFlags}
        />
      </Modal>

      {/* Add Skills Modal */}
      <Modal
        isOpen={showSkillsModal}
        onClose={() => {
          setShowSkillsModal(false);
          setSelectedSession(null);
          setSelectedCategories([]);
        }}
        title={`Add Skills to ${selectedSession?.name || "Session"}`}
        size="lg"
      >
        {selectedSession && (
          <div className="space-y-6">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-slate-900 mb-1">
                Session Information
              </h4>
              <p className="text-sm text-slate-800">
                {selectedSession.name} •{" "}
                {formatLocalDate(selectedSession.session_date)}
                {selectedSession.start_time &&
                  ` • ${formatTime(selectedSession.start_time)}`}
              </p>
            </div>

            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Select Skill Categories to Add
              </h4>

              {skillCategories.length === 0 ? (
                <div className="text-center py-8">
                  <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No skill categories available
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Create skill categories in the Skills Management section
                    first.
                  </p>
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/portal/club/${clubId}/skills`)}
                    >
                      Go to Skills Management
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {skillCategories.map((category) => (
                    <div
                      key={category.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedCategories.includes(category.id)
                          ? "border-slate-500 bg-slate-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => handleCategoryToggle(category.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                          <div>
                            <h5 className="font-medium text-gray-900">
                              {category.name}
                            </h5>
                            {category.description && (
                              <p className="text-sm text-gray-600">
                                {category.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(category.id)}
                            onChange={() => handleCategoryToggle(category.id)}
                            className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedCategories.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <h5 className="text-sm font-medium text-gray-900 mb-2">
                  Selected Categories ({selectedCategories.length})
                </h5>
                <div className="flex flex-wrap gap-2">
                  {selectedCategories.map((categoryId) => {
                    const category = skillCategories.find(
                      (c) => c.id === categoryId,
                    );
                    return category ? (
                      <Badge key={categoryId} variant="blue">
                        {category.name}
                      </Badge>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowSkillsModal(false);
                  setSelectedSession(null);
                  setSelectedCategories([]);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveSkills}
                disabled={selectedCategories.length === 0}
              >
                Add Selected Categories ({selectedCategories.length})
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Groups Selection Modal */}
      <GroupsSelectionModal
        isOpen={showGroupsModal}
        onClose={() => {
          setShowGroupsModal(false);
          setSelectedSession(null);
        }}
        session={selectedSession}
        clubId={clubId}
        onGroupsUpdated={loadEvaluationData}
      />

      {/* Delete Session Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteSessionModal}
        onClose={() => {
          setShowDeleteSessionModal(false);
          setSessionToDelete(null);
        }}
        onConfirm={handleDeleteSession}
        title="Delete Session"
        message={
          sessionToDelete
            ? `Are you sure you want to delete "${sessionToDelete.name}"? This will remove all check-ins and results.`
            : "Are you sure you want to delete this session?"
        }
        confirmText="Delete Session"
        loading={deletingSession}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleEvaluationDelete}
        title="Delete Evaluation"
        message={`Are you sure you want to delete "${evaluation.name}"? This action cannot be undone and will permanently remove all associated data.`}
        confirmText="Delete Evaluation"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={deleting}
      />
    </div>
  );
};

export default EvaluationSetup;
